import React from "react";
import { Link } from "react-router-dom";

/**
 * Shared shell for login/register/reset screens.
 * Dark charcoal background with rose glow — matches the rest of the store.
 */
const AuthShell = ({ eyebrow, title, subtitle, children, footer }) => (
  <main className="min-h-screen bg-charcoal flex items-center justify-center px-6 py-24 relative overflow-hidden">
    <div
      className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-[0.14] blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, #D89999 0%, transparent 60%)" }}
    />
    <div className="relative w-full max-w-md">
      {eyebrow && (
        <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3 text-center">// {eyebrow}</p>
      )}
      <h1 className="font-serif-display italic text-5xl md:text-6xl text-off-white text-center leading-[0.95]">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-4 text-off-white/60 font-light text-center">{subtitle}</p>
      )}
      {children}
      <div className="mt-8 text-center space-y-3">
        {footer}
        <Link to="/" className="block font-mono text-[10px] tracking-[0.3em] text-off-white/50 hover:text-rose transition-colors-only" data-testid="auth-back-home">
          ← BACK TO STORE
        </Link>
      </div>
    </div>
  </main>
);

export default AuthShell;

export const AuthLabel = ({ children }) => (
  <label className="font-mono text-[10px] tracking-[0.3em] text-off-white/60 mb-2 block">{children}</label>
);

export const AuthInput = React.forwardRef((props, ref) => (
  <input
    ref={ref}
    {...props}
    className="w-full h-12 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-4 text-off-white transition-colors-only"
  />
));
AuthInput.displayName = "AuthInput";

export const AuthPrimaryButton = ({ loading, children, ...rest }) => (
  <button
    {...rest}
    disabled={rest.disabled || loading}
    className="w-full h-14 bg-rose text-charcoal font-mono text-[12px] tracking-[0.25em] font-semibold flex items-center justify-center gap-2 hover:bg-off-white transition-colors-only disabled:opacity-60"
  >
    {loading ? "…" : children}
  </button>
);

export const AuthErrorLine = ({ error }) =>
  error ? (
    <p className="font-mono text-[11px] tracking-[0.2em] text-rose-deep" data-testid="auth-error">
      // {String(error).toUpperCase()}
    </p>
  ) : null;

// Formats FastAPI's `detail` field into a plain string (handles 422 arrays)
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const GoogleSignInButton = () => {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={start}
      data-testid="google-signin-btn"
      className="w-full h-12 bg-off-white text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold flex items-center justify-center gap-3 hover:bg-rose transition-colors-only"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      CONTINUE WITH GOOGLE
    </button>
  );
};
